import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Upload } from "lucide-react";
import { inputCls } from "./SectionEditor";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const IMAGE_FIELDS = [
  ["heroTexture", "Hero texture background"],
  ["skyline", "Trust section skyline"],
  ["logoFull", "Full horizontal logo"],
  ["logoMark", "Logo mark (badge tile)"],
];

export function ImageEditor({ doc, setDoc, token }) {
  const [uploading, setUploading] = useState("");
  const fileRefs = useRef({});

  const upload = async (key, file) => {
    if (!file) return;
    setUploading(key);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/uploads`, fd, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      const url = `${BACKEND}/api/files/${r.data.path}`;
      setDoc({ ...doc, images: { ...doc.images, [key]: url } });
      toast.success("Image uploaded — remember to Save & Publish.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading("");
    }
  };

  return (
    <div data-testid="admin-image-editor">
      <h1 className="mb-2 font-display text-2xl font-semibold">Images</h1>
      <p className="mb-6 text-sm text-slate-500">Upload from your computer or paste an image URL.</p>
      <div className="grid gap-6 md:grid-cols-2">
        {IMAGE_FIELDS.map(([key, label]) => (
          <div key={key} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <div className="mb-2 text-xs font-bold uppercase tracking-[0.1em] text-slate-500">{label}</div>
            <div className="mb-3 flex h-36 items-center justify-center overflow-hidden rounded-xl border border-slate-200 bg-white">
              <img src={doc.images[key]} alt={label} className="max-h-full max-w-full object-contain" data-testid={`admin-image-preview-${key}`} />
            </div>
            <div className="flex items-center gap-2">
              <input
                data-testid={`admin-image-input-${key}`}
                value={doc.images[key]}
                onChange={(e) => setDoc({ ...doc, images: { ...doc.images, [key]: e.target.value } })}
                className={inputCls}
                placeholder="https://… or /assets/…"
              />
              <input
                type="file"
                accept="image/*"
                className="hidden"
                ref={(el) => (fileRefs.current[key] = el)}
                onChange={(e) => upload(key, e.target.files?.[0])}
              />
              <button
                data-testid={`admin-image-upload-${key}`}
                onClick={() => fileRefs.current[key]?.click()}
                disabled={uploading === key}
                className="flex h-[42px] w-[46px] shrink-0 items-center justify-center rounded-xl bg-navy text-white transition-colors hover:bg-midnight disabled:opacity-60"
                aria-label={`Upload ${label}`}
              >
                {uploading === key ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
